import BrandclubPhoneInput, { BCPhoneInputVariant } from '../BrandclubPhoneInput';
import { PopupDrawer } from '../PopupDrawer/PopupDrawer';
import React, { useEffect, useState } from 'react';
import { RewardTable } from './RewardTable';
import { ArrowLink } from './ArrowLink';
import { BrandclubVideo } from '../BrandclubVideo';
import { SharePage } from './SharePage';
import { buildTrackingParams, trackRetailerProductPageEvent } from '../../../utils/analytics';
import { getAssetNameFromUrl } from '../../../utils/assetUtils';
import { ProductPageDialogSuccess } from './ProductPageDialogSuccess';
import { ProductPageDialogContent } from './ProductPageDialogContent';
import { AnalyticsTrackingEvent } from '../../../analytics/events';
import { ProductPageDialogProps } from './ProductPageDialogType';

export const ProductPageDialogMain = ({
  popupDrawerProps: { open, onClose },
  showIcon,
  imageUrl,
  imageSize = 51,
  title,
  secondTitle,
  subtitle,
  phoneInput,
  redirect,
  sharePage,
  sharePageUrl,
  onShareLinkCopied,
  rewardTableRows,
  video,
  Link,
  mainEntity,
  trackingParams,
  retailerPDPNavigationEventParams,
  trackRetailerPDPNavigation,
  analyticsTrack,
  handleSendText,
  userProfile,
}: ProductPageDialogProps) => {
  const [step, setStep] = useState(0);

  const onTextSent = () => setStep(1);

  const handleClose = () => {
    if (step !== 0) {
      setStep(0);
    }
    onClose();
  };

  const trackVideoInteraction = (eventName: AnalyticsTrackingEvent, additionalEventProps = {}) => {
    const videoId = getAssetNameFromUrl(video?.videoUrl || '');
    if (videoId && video?.videoUrl && trackingParams && mainEntity && analyticsTrack) {
      const eventProps = {
        ...buildTrackingParams({ trackingParams, mainEntity }),
        videoId,
        video: video.videoUrl,
        videoTitle: title,
        ...additionalEventProps,
      };
      analyticsTrack(eventName, eventProps);
    }
  };

  const handleVideoPlay = () => {
    trackVideoInteraction(AnalyticsTrackingEvent.LANDING_PAGE_VIDEO_PLAY_STARTED);
  };

  const handleVideoPause = ({ currentTime }: { currentTime: number }) => {
    trackVideoInteraction(AnalyticsTrackingEvent.LANDING_PAGE_VIDEO_PLAY_PAUSED, {
      videoPlayDuration: parseFloat(currentTime.toFixed(2)),
    });
  };

  const handleVideoEnd = ({ duration }: { duration: number }) => {
    trackVideoInteraction(AnalyticsTrackingEvent.LANDING_PAGE_VIDEO_PLAY_ENDED, {
      videoPlayDuration: parseFloat(duration.toFixed(2)),
    });
  };

  const handleTrackRetailerPDPNavigation = () => {
    if (retailerPDPNavigationEventParams && trackingParams && !!analyticsTrack) {
      const params = {
        ...trackingParams,
        ...retailerPDPNavigationEventParams,
      };
      trackRetailerProductPageEvent({
        trackingEventAction: 'navigateToRetailerProductPage',
        trackingParams: params,
        analyticsTrack,
      });
    }
  };

  useEffect(() => {
    let autoRedirectTimeout: NodeJS.Timeout | undefined = undefined;
    if (!!phoneInput?.autoRedirectOnSuccess && phoneInput?.redirectionUrl && step === 1) {
      autoRedirectTimeout = setTimeout(() => {
        window.open(phoneInput?.redirectionUrl);
      }, 2000);
    }
    return () => {
      if (autoRedirectTimeout) {
        clearTimeout(autoRedirectTimeout);
      }
    };
  }, [phoneInput?.autoRedirectOnSuccess, phoneInput?.redirectionUrl, step]);

  return (
    <PopupDrawer open={open} onClose={handleClose}>
      {step === 0 ? (
        <ProductPageDialogContent
          title={title}
          secondTitle={secondTitle}
          subtitle={subtitle}
          showIcon={showIcon}
          imageUrl={imageUrl}
          imageSize={imageSize}
          sx={{
            paddingBottom: `${sharePage && video ? '30' : '48'}px`,
          }}
        >
          {phoneInput && handleSendText && (
            <div className="phone-input-container">
              <BrandclubPhoneInput
                userProfile={userProfile}
                hasHeader={false}
                buttonText={'Get App'}
                variant={BCPhoneInputVariant.SM}
                onTextSent={onTextSent}
                handleSendText={handleSendText}
              />
            </div>
          )}
          {redirect && (
            <ArrowLink
              {...{
                text: redirect.text,
                url: redirect.url,
                onTextClick: () => {
                  if (trackRetailerPDPNavigation) {
                    handleTrackRetailerPDPNavigation();
                  }
                  window.open(redirect.url, '_blank');
                },
              }}
            />
          )}
          {sharePage && (
            <div className="share-container">
              <SharePage url={sharePageUrl || window.location.href} onShareLinkCopied={onShareLinkCopied} />
            </div>
          )}
          {rewardTableRows && <RewardTable rows={rewardTableRows} containerStyle={{ marginTop: 10 }} />}
          {video && (
            <div className="video-container" style={{ margin: `${sharePage && video ? '0' : '12'}px auto 0` }}>
              <BrandclubVideo
                videoUrl={video.videoUrl}
                posterUrl={video.coverImageUrl}
                canPlay
                onPlay={handleVideoPlay}
                onPause={handleVideoPause}
                onEnd={handleVideoEnd}
              />
            </div>
          )}
        </ProductPageDialogContent>
      ) : (
        <ProductPageDialogSuccess
          handleClose={handleClose}
          closeDialogText={phoneInput?.closeDialogText}
          redirectionText={phoneInput?.redirectionText}
          redirectionUrl={Link ? phoneInput?.redirectionUrl : undefined}
        />
      )}
    </PopupDrawer>
  );
};

export const ProductPageDialog = (props: ProductPageDialogProps) => {
  const location = window.location;
  useEffect(() => {
    props.popupDrawerProps.open && props.popupDrawerProps.onClose();
    // close the dialog whenever the location changes
  }, [location]);

  return <ProductPageDialogMain {...props} />;
};
